import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, MagnifyingGlass, Wallet, PiggyBank, HandCoins } from "@phosphor-icons/react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";

const TIPE_SIMPANAN = [
  { value: "pokok", label: "Simpanan Pokok", icon: Wallet, color: "text-[#B8860B]", bg: "bg-[#B8860B]/10" },
  { value: "wajib", label: "Simpanan Wajib", icon: PiggyBank, color: "text-[#008080]", bg: "bg-[#008080]/10" },
  { value: "sukarela", label: "Simpanan Sukarela", icon: HandCoins, color: "text-purple-600", bg: "bg-purple-50" },
];

export default function SimpananPage() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filterTipe, setFilterTipe] = useState("__all__");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ anggota_id: "", anggota_nama: "", tipe: "pokok", jumlah: 0, tanggal: "", keterangan: "" });

  const load = useCallback(async () => {
    const tp = filterTipe !== "__all__" ? `&tipe=${filterTipe}` : "";
    const [s, sm, u] = await Promise.all([
      api.get(`/simpanan?page=${page}&limit=15&search=${search}${tp}`),
      api.get("/simpanan/summary"),
      api.get("/users?limit=500").catch(() => ({ data: { data: [] } }))
    ]);
    setItems(s.data.data);
    setTotal(s.data.total);
    setSummary(sm.data);
    setUsers(u.data.data || []);
  }, [page, search, filterTipe]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/simpanan", form);
      toast.success("Simpanan berhasil dicatat (jurnal otomatis dibuat)");
      setDialogOpen(false);
      setForm({ anggota_id: "", anggota_nama: "", tipe: "pokok", jumlah: 0, tanggal: "", keterangan: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const fmt = (v) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(v || 0);

  return (
    <div data-testid="simpanan-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "Work Sans" }}>Simpanan Anggota</h1>
          <p className="text-sm text-slate-500 mt-1">Kelola simpanan pokok, wajib, dan sukarela anggota koperasi</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild><Button className="bg-[#B8860B] hover:bg-[#9A7209] text-white"><Plus size={18} className="mr-2" /> Catat Simpanan</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle style={{ fontFamily: "Work Sans" }}>Simpanan Baru</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Anggota</Label>
                <Select value={form.anggota_id} onValueChange={(v) => { const u = users.find(x => x._id === v); setForm({ ...form, anggota_id: v, anggota_nama: u?.name || "" }); }}>
                  <SelectTrigger><SelectValue placeholder="Pilih anggota" /></SelectTrigger>
                  <SelectContent>{users.map(u => <SelectItem key={u._id} value={u._id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Jenis Simpanan</Label>
                  <Select value={form.tipe} onValueChange={(v) => setForm({ ...form, tipe: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{TIPE_SIMPANAN.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Jumlah (Rp)</Label><Input type="number" value={form.jumlah} onChange={e => setForm({ ...form, jumlah: parseFloat(e.target.value) || 0 })} required /></div>
              </div>
              <div className="space-y-2"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Tanggal</Label><Input type="date" value={form.tanggal} onChange={e => setForm({ ...form, tanggal: e.target.value })} required /></div>
              <div className="space-y-2"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Keterangan</Label><Input value={form.keterangan} onChange={e => setForm({ ...form, keterangan: e.target.value })} /></div>
              <div className="p-3 bg-blue-50 rounded-lg text-xs text-blue-700">Jurnal akuntansi akan otomatis dibuat: Debit Kas, Kredit Simpanan {TIPE_SIMPANAN.find(t=>t.value===form.tipe)?.label}</div>
              <Button type="submit" className="w-full bg-[#B8860B] hover:bg-[#9A7209] text-white">Simpan</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        {TIPE_SIMPANAN.map(t => {
          const data = summary?.per_tipe?.[t.value];
          return (
            <Card key={t.value} className="bg-white border border-slate-200">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${t.bg} flex items-center justify-center`}><t.icon size={22} weight="duotone" className={t.color} /></div>
                <div>
                  <p className="text-lg font-bold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>{fmt(data?.total || 0)}</p>
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{t.label} ({data?.count || 0})</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
        <Card className="bg-[#B8860B]/5 border border-[#B8860B]/20">
          <CardContent className="p-4 text-center">
            <p className="text-xl font-bold text-[#B8860B]" style={{ fontFamily: "Work Sans" }}>{fmt(summary?.total || 0)}</p>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Total Simpanan</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-64"><MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input placeholder="Cari anggota..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9" /></div>
        <Select value={filterTipe} onValueChange={v => { setFilterTipe(v); setPage(1); }}><SelectTrigger className="w-[180px] h-9"><SelectValue placeholder="Semua Jenis" /></SelectTrigger><SelectContent><SelectItem value="__all__">Semua Jenis</SelectItem>{TIPE_SIMPANAN.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select>
      </div>

      {/* Table */}
      <Card className="bg-white border border-slate-200"><CardContent className="p-0">
        <Table><TableHeader><TableRow className="bg-slate-50">
          <TableHead className="text-xs uppercase tracking-wider font-semibold">Tanggal</TableHead>
          <TableHead className="text-xs uppercase tracking-wider font-semibold">Anggota</TableHead>
          <TableHead className="text-xs uppercase tracking-wider font-semibold">Jenis</TableHead>
          <TableHead className="text-xs uppercase tracking-wider font-semibold text-right">Jumlah</TableHead>
          <TableHead className="text-xs uppercase tracking-wider font-semibold">Keterangan</TableHead>
          <TableHead className="text-xs uppercase tracking-wider font-semibold">Status</TableHead>
        </TableRow></TableHeader><TableBody>
          {items.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Belum ada data simpanan</TableCell></TableRow> :
          items.map(s => (
            <TableRow key={s._id} className="hover:bg-slate-50">
              <TableCell className="text-xs">{s.tanggal}</TableCell>
              <TableCell className="text-sm font-medium">{s.anggota_nama}</TableCell>
              <TableCell><Badge className={`text-[10px] ${s.tipe==="pokok"?"bg-[#B8860B]/10 text-[#B8860B]":s.tipe==="wajib"?"bg-[#008080]/10 text-[#008080]":"bg-purple-50 text-purple-600"} hover:bg-inherit`}>{s.tipe}</Badge></TableCell>
              <TableCell className="text-sm text-right font-semibold text-[#B8860B]">{fmt(s.jumlah)}</TableCell>
              <TableCell className="text-xs text-slate-500">{s.keterangan || "-"}</TableCell>
              <TableCell><Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">{s.status}</Badge></TableCell>
            </TableRow>
          ))}
        </TableBody></Table>
        <div className="px-4 pb-3"><Pagination page={page} limit={15} total={total} onPageChange={setPage} /></div>
      </CardContent></Card>
    </div>
  );
}
